import { ReactElement } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Label } from 'components/v2/Label'
import { IDowSelectorProps } from './types'

const DOW = new Array(7).fill('').map((_, index) => {
  return index + 1
})

const DowSelectorContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
`

const DowRow = styled.div`
  display: flex;
  gap: 8px;
`

const getDayColor = (isSelected?: boolean, isEditable?: boolean): string => {
  if (isSelected && !isEditable) {
    return '#B0B0B0'
  }
  if (isSelected) {
    return '#A86EEB'
  }
  return '#F0F0F0'
}

const Day = styled.div<{ isSelected?: boolean; isEditable?: boolean }>`
  height: 32px;
  width: 32px;
  text-align: center;
  font-size: 12px;
  border-radius: 50%;
  color: ${(props): string => (props.isSelected ? '#ffffff' : '#28265D')};
  background-color: ${({ isSelected, isEditable }): string => getDayColor(isSelected, isEditable)};
  border: ${({ isSelected, isEditable }): string => getDayColor(isSelected, isEditable)};
  cursor: ${({ isEditable }): string => (isEditable ? 'pointer' : 'not-allowed')};
  align-items: center;
  justify-content: center;
  display: inline-flex;

  &:hover {
    background-color: ${(props): string => (props.isSelected ? '#A86EEB' : '#F4F0FF')};
    border: ${(props): string => (props.isSelected ? '#A86EEB' : '#F0F0F0')};
  }
`

const messages = defineMessages({
  monday: {
    id: 'dayOfWeek.monday',
    defaultMessage: 'Monday'
  },
  tuesday: {
    id: 'dayOfWeek.tuesday',
    defaultMessage: 'Tuesday'
  },
  wednesday: {
    id: 'dayOfWeek.wednesday',
    defaultMessage: 'Wednesday'
  },
  thursday: {
    id: 'dayOfWeek.thursday',
    defaultMessage: 'Thursday'
  },
  friday: {
    id: 'dayOfWeek.friday',
    defaultMessage: 'Friday'
  },
  saturday: {
    id: 'dayOfWeek.saturday',
    defaultMessage: 'Saturday'
  },
  sunday: {
    id: 'dayOfWeek.sunday',
    defaultMessage: 'Sunday'
  }
})

export const getDayNumber = (day: string): number => {
  switch (day.toLowerCase()) {
    case 'monday':
      return 1
    case 'tuesday':
      return 2
    case 'wednesday':
      return 3
    case 'thursday':
      return 4
    case 'friday':
      return 5
    case 'saturday':
      return 6
    case 'sunday':
      return 7
  }
  return -1
}

export const mapDowToNumber = (dow: string[]): number[] => {
  return dow.map((day) => getDayNumber(day)).sort()
}

const DowSelector = ({ label, values, onChange, errors, ...props }: IDowSelectorProps): ReactElement => {
  const intl = useIntl()
  const isShowingError = Boolean(errors)
  const DAYS_OF_WEEK = [
    intl.formatMessage(messages.monday),
    intl.formatMessage(messages.tuesday),
    intl.formatMessage(messages.wednesday),
    intl.formatMessage(messages.thursday),
    intl.formatMessage(messages.friday),
    intl.formatMessage(messages.saturday),
    intl.formatMessage(messages.sunday)
  ]

  const isEditable = !props.disabled && !props.readOnly

  const toggleDay = (day: number): void => {
    if (!isEditable) return

    let newValues
    if (values.indexOf(day) === -1) {
      newValues = [...values, day]
    } else {
      newValues = values.filter((value) => {
        return value !== day
      })
    }

    if (onChange) {
      onChange(newValues)
    }
  }

  return (
    <DowSelectorContainer>
      {label ? <Label>{label}</Label> : null}
      <DowRow>
        {DOW.map((day) => {
          return (
            <Day
              key={day}
              onClick={toggleDay.bind(this, day)}
              isSelected={values.indexOf(day) > -1}
              isEditable={isEditable}
            >
              {`${DAYS_OF_WEEK[day - 1][0]}`}
            </Day>
          )
        })}
      </DowRow>
      {isShowingError && <ErrorMessage>{errors}</ErrorMessage>}
    </DowSelectorContainer>
  )
}

export { DowSelector }
